// Bubble text content for Mission 5 with highlights, keyboard layout conditionals, and fellow emotion.
// Bubble size is automatic based on text length.
// Optional per-page bubble layout overrides:
// - bubbleLeftPercent / bubbleBottomPercent
// - bubbleMinWidthPercent / bubbleMaxWidthPercent
// Optional per-page fellow emotion:
// - fellowEmotion: 'hq-happy' | 'hq-impressed' | 'hq-neutral' | 'hq-speaking'
// Each entry: { text: string, highlights: Array<{text?: string, conditional?: object}> }

const DEFAULT_LAYOUT_LABELS = {
 KeyZ: "Z",
 Slash: "/",
 KeyS: "S",
 KeyD: "D",
 KeyF: "F",
 KeyK: "K",
 KeyL: "L",
 Semicolon: ";",
};

export const MISSION_FIVE_BUBBLE_TEXT = [
 // 1
 {
  text: `Congratulations on your promotion to chief! Now you get to deal with gangs.\n\nYou can either work alone or team up with trucks.\n\nYour success depends on your choices and the truck's choices.`,
  audioSrc: null,
  fellowEmotion: "hq-happy",
  highlights: [
   { text: "gangs" },
   { text: "alone" },
   { text: "team up" },
  ],
 },
 // 2
 {
  text: `You will see two gangs in front. First decide whether to chase them:\n\n  • together with a partner next to you: press key "{{Slash}}"\n  • alone: press key "{{KeyZ}}"\n\nNext, I will show you how to chase gangs alone.`,
  audioSrc: null,
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "gangs" },
   { text: "together with a partner next to you:" },
   { text: "alone:" },
   { text: "Next, I will show you how to chase gangs alone." },
   { conditional: { key: "Slash" } },
   { conditional: { key: "KeyZ" } },
  ],
 },
 // 3
 {
  text: `Chasing alone:\n\nFocus on the orange points and decide which gang to chase. Ignore the purple points because you can only get them when chasing with a partner.`,
  audioSrc: null,
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "Chasing alone:" },
   { text: "orange points" },
   { text: "decide which gang" },
  ],
 },
 // 4
 {
  text: `Once you have selected a gang, pass them either on the left or right side.\n\nYou will earn the orange points above the package.`,
  audioSrc: null,
  fellowEmotion: "hq-neutral",
  highlights: [
   { text: "left or right side" },
   { text: "orange points" },
  ],
 },
 // 5
 {
  text: `However, if you enter the same lane as the gang, you'll crash!`,
  audioSrc: null,
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "However" },
   { text: "same lane as the gang, you'll crash!" },
  ],
 },
 // 6
 {
  text: `Chasing together:\n\nFirst, propose a lane for your partner. To do this, press a response key (see response footer in your dashboard at the bottom).`,
  audioSrc: null,
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "Chasing together:" },
   { text: "your partner." },
   { text: "press a response key" },
  ],
 },
 // 7
 {
  text: `Next, choose a lane for yourself.\n\nIf you both surround the same gang, you each earn the purple points in your chosen lane.`,
  audioSrc: null,
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "yourself." },
   { text: "surround" },
   { text: "same gang," },
   { text: "purple points" },
  ],
 },
 // 8
 {
  text: `A side note:\n\nYour partner sees your proposed lane through the gray window. The signal quality can vary: sometimes it's clear, sometimes distorted.`,
  audioSrc: null,
  fellowEmotion: "hq-neutral",
  highlights: [
   { text: "partner" },
   { text: "gray window" },
   { text: "vary:" },
  ],
 },
 // 9
 {
  text: `Some of the trucks are agents on our side but others might be from the mafia. Mafia trucks might not agree to work with you. Sometimes, teaming up can be helpful, but other times it isn't. Use your discretion.\n\n  • If you chase different gangs, you both receive orange points instead.\n  • But if your partner crashes, only you will earn points.`,
  audioSrc: null,
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "our side" },
   { text: "mafia" },
   { text: "might not agree" },
   { text: "helpful" },
   { text: "isn't. Use your discretion." },
   { text: "different gangs" },
   { text: "orange points" },
   { text: "partner crashes" },
   { text: "you" },
  ],
 },
 // 10
 {
  text: `Good luck, chief!\n\nTip: After you moved into a lane, you can speed things up by pressing the "{{Slash}}" key!`,
  audioSrc: null,
  fellowEmotion: "hq-happy",
  highlights: [
   { text: "Tip:" },
   { text: "speed things up" },
   { text: "key" },
   { conditional: { key: "Slash" } },
  ],
 },
];

function buildLabelsFromLayoutConfig(layoutConfig) {
 return {
  ...DEFAULT_LAYOUT_LABELS,
  KeyZ: layoutConfig?.backwardSlowDownKey ?? DEFAULT_LAYOUT_LABELS.KeyZ,
  Slash: layoutConfig?.forwardSpeedUpKey ?? DEFAULT_LAYOUT_LABELS.Slash,
  KeyS: layoutConfig?.KeyS ?? DEFAULT_LAYOUT_LABELS.KeyS,
  KeyD: layoutConfig?.KeyD ?? DEFAULT_LAYOUT_LABELS.KeyD,
  KeyF: layoutConfig?.KeyF ?? DEFAULT_LAYOUT_LABELS.KeyF,
  KeyK: layoutConfig?.KeyK ?? DEFAULT_LAYOUT_LABELS.KeyK,
  KeyL: layoutConfig?.KeyL ?? DEFAULT_LAYOUT_LABELS.KeyL,
  Semicolon: layoutConfig?.Semicolon ?? DEFAULT_LAYOUT_LABELS.Semicolon,
 };
}

export function getKeyLabel(key, layoutConfig) {
 const labels = buildLabelsFromLayoutConfig(layoutConfig);
 return labels[key] || key;
}
